import {
  ControlMessage,
  InAppMessage,
  logInAppMessageClick,
  logInAppMessageImpression,
  showInAppMessage,
  subscribeToInAppMessage,
} from "@braze/web-sdk";
import { TOPICS, eventDispatcher } from "@origin-digital/event-dispatcher";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { logger } from "@origin-digital/reporting-client";

import { BRAZE_ID } from "../utils/config";
import { configureButtons, overwriteButtonAction } from "./buttons";
import { overwriteMessage } from "./message";
import { brazeIAMStyle } from "./inAppMessageStyles";
import {
  isAllowedToShow,
  isCompletionSurveyMessage,
  shouldHideInNative,
  shouldHideInWeb,
  isSurveyPath,
  LOG_MESSAGES,
  isInAppMessage,
  hasButtons,
} from "./inAppMessageUtils";

export const configureMessage = (inAppMessage: InAppMessage) => {
  inAppMessage.htmlId = BRAZE_ID;

  // turn off any buttons with navTo. or navClose.
  if (hasButtons(inAppMessage)) {
    const replacementButtons = configureButtons(inAppMessage.buttons);
    inAppMessage.buttons = replacementButtons;
  }

  const { extras } = inAppMessage;

  // iframed survey detection - unable to confirm type
  if (extras && extras.style === "oe-style") {
    inAppMessage.css = brazeIAMStyle;
  }

  // confetti - if IAM has extras.showConfetti = "true"
  // trigger event dispatcher to show confetti
  if (extras?.showConfetti === "true") {
    // fire event dispatcher to show confetti
    eventDispatcher.dispatch({
      topic: TOPICS.CONFETTI_SHOW,
      payload: {
        particleCount: extras.particleCount
          ? Number(extras.particleCount)
          : undefined,
        spread: extras.spread ? Number(extras.spread) : undefined,
      },
    });
  }
};

export const handleSurveyMessage = (inAppMessage: InAppMessage) => {
  logger.debug("[braze] task completion survey triggered");

  if (!inAppMessage.message) {
    logger.debug("[braze] task completion survey payload empty");
    return;
  }

  try {
    const values = JSON.parse(inAppMessage.message);
    const { heading, message, cta, surveyType, dialogHeight } = values;

    const onClickHandler = () => {
      logger.debug("[braze] task completion survey clicked");
      // inform Braze that the message was clicked
      logInAppMessageClick(inAppMessage);
      // fire an event to open the survey
      eventDispatcher.dispatch({
        topic: TOPICS.SURVEY_INIT,
        payload: { surveyType, dialogHeight },
      });
    };

    eventDispatcher.dispatch({
      topic: TOPICS.ALERT_FOOTER_OPEN,
      payload: {
        heading,
        message,
        cta: {
          buttonText: cta,
          onClick: onClickHandler,
        },
        onShow: () => logInAppMessageImpression(inAppMessage),
      },
    });
  } catch (error) {
    logger.error("[braze] error triggering task completion survey", error);
  }
};

export const inAppMessageSubscribe = (
  inAppMessage: InAppMessage | ControlMessage,
  environment: EnvironmentNames,
  path: string
) => {
  try {
    if (isSurveyPath(path)) {
      // Handle all early exits
      logger.debug(LOG_MESSAGES.SURVEY_APP_IGNORE);
      return;
    }

    if (shouldHideInWeb(inAppMessage?.extras)) {
      logger.debug(LOG_MESSAGES.PREVENT_WEB);
      return;
    }

    if (shouldHideInNative(inAppMessage?.extras)) {
      logger.debug(LOG_MESSAGES.PREVENT_NATIVE);
      return;
    }

    // Handle all special message types
    if (isCompletionSurveyMessage(path, environment, inAppMessage.extras)) {
      if (isInAppMessage(inAppMessage)) {
        handleSurveyMessage(inAppMessage);
      }
      return;
    }

    if (inAppMessage.isControl) {
      // This will log an impression and not show a visible message
      logger.debug(LOG_MESSAGES.CONTROL_GROUP);
      showInAppMessage(inAppMessage);
      return;
    }

    // Main flow - only execute if all guards pass
    if (isAllowedToShow(path, environment)) {
      // configure before show
      configureMessage(inAppMessage);
      // braze call to show message
      showInAppMessage(inAppMessage, undefined, () => {
        overwriteButtonAction(inAppMessage);
        overwriteMessage(inAppMessage);
      });
    }
  } catch (error) {
    logger.error(LOG_MESSAGES.ERROR, error);
  }
};

export const inAppMessageInit = (
  environment: EnvironmentNames,
  path: string
) => {
  subscribeToInAppMessage((inAppMessage) =>
    inAppMessageSubscribe(inAppMessage, environment, path)
  );
};
